import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as ArchiveIcon,
  EditOutlined as EditIcon,
  InboxOutlined as InboxIcon,
  ListAltOutlined as TableIcon,
  LocationOnOutlined as PageIcon,
  MapOutlined as MapIcon,
  OpenInNewOutlined as OpenIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import CollectionPointFormDialog from '../../components/collection-points/CollectionPointFormDialog';
import CollectionPointsMap from '../../components/collection-points/CollectionPointsMap';
import BulkImportControls from '../../components/common/BulkImportControls';
import LabelChips from '../../components/common/LabelChips';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import PageHeader from '../../components/PageHeader';
import PaginationControl from '../../components/PaginationControl';
import { APP_SHAPE, elevatedPanelSx } from '../../constants/shape';
import { useAuthPermissions } from '../../hooks/useAuthPermissions';
import { downloadCollectionPointImportTemplate, importCollectionPoints, listCollectionPoints } from '../../services/collection-point.service';
import { listSuppliers } from '../../services/supplier.service';
import { getWasteCatalogMaterialOptions, type WasteCatalogOption } from '../../services/waste-catalog.service';
import { useConfirm } from '../../hooks/useConfirm';
import { useCollectionPointStore } from '../../stores/collection-point.store';
import { useOrganizationStore } from '../../stores/organization.store';
import type { Supplier, SupplierCollectionPoint, SupplierCollectionPointInput } from '../../types/supplier.type';

type CollectionPointsView = 'table' | 'map';
const PAGE_SIZE = 20;

export default function CollectionPoints() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const {
    collectionPoints,
    total,
    isLoading,
    isSaving,
    error,
    clearError,
    fetchCollectionPoints,
    createCollectionPoint,
    updateCollectionPoint,
    archiveCollectionPoint,
  } = useCollectionPointStore();
  const { canMutate } = useAuthPermissions();
  const { confirm, confirmDialog } = useConfirm();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [materialId, setMaterialId] = useState('');
  const [city, setCity] = useState('');
  const [groupBy, setGroupBy] = useState<'none' | 'city' | 'supplier'>('none');
  const [supplierOptions, setSupplierOptions] = useState<Supplier[]>([]);
  const [materialOptions, setMaterialOptions] = useState<WasteCatalogOption[]>([]);
  const [cityOptions, setCityOptions] = useState<string[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<SupplierCollectionPoint | null>(null);

  const orgId = selectedOrganization?.id;
  const view: CollectionPointsView = searchParams.get('view') === 'map' ? 'map' : 'table';
  const canCreate = Boolean(orgId && canMutate(`recyclee.${orgId}.*.supplier.create`));
  const canEdit = Boolean(orgId && canMutate(`recyclee.${orgId}.*.supplier.write`));
  const canArchive = Boolean(orgId && canMutate(`recyclee.${orgId}.*.supplier.delete`));

  useEffect(() => {
    if (!orgId) return;
    void listSuppliers({ organization_id: orgId, limit: 100 }).then((result) => setSupplierOptions(result.suppliers)).catch(() => setSupplierOptions([]));
    void getWasteCatalogMaterialOptions(orgId).then(setMaterialOptions).catch(() => setMaterialOptions([]));
    void listCollectionPoints({ organization_id: orgId, limit: 500 })
      .then((result) => {
        const cities = Array.from(new Set(result.collection_points.map((point) => point.city).filter((value): value is string => Boolean(value))));
        setCityOptions(cities.sort((a, b) => a.localeCompare(b)));
      })
      .catch(() => setCityOptions([]));
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    void fetchCollectionPoints({
      organization_id: orgId,
      page: view === 'map' ? 1 : page,
      limit: view === 'map' ? 500 : PAGE_SIZE,
      search: search.trim() || undefined,
      supplier_id: supplierId || undefined,
      material_id: materialId || undefined,
      city: city || undefined,
    });
  }, [orgId, page, search, supplierId, materialId, city, view, fetchCollectionPoints]);

  useEffect(() => setPage(1), [orgId, search, supplierId, materialId, city]);

  const supplierNameById = useMemo(() => new Map(supplierOptions.map((supplier) => [supplier.id, supplier.name])), [supplierOptions]);
  const materialLabels = useMemo(() => new Map(materialOptions.map((option) => [option.value, option.label])), [materialOptions]);
  const hasActiveFilters = Boolean(search || supplierId || materialId || city);

  const groupedRows = useMemo(() => {
    if (groupBy === 'none') {
      return collectionPoints.map((point) => ({ point, groupLabel: null as string | null }));
    }
    const keyFor = (point: SupplierCollectionPoint) =>
      groupBy === 'city' ? (point.city || t('suppliers.collectionPoints.list.noCity')) : (supplierNameById.get(point.supplier_id) ?? t('suppliers.collectionPoints.list.noCity'));
    const sorted = [...collectionPoints].sort((a, b) => keyFor(a).localeCompare(keyFor(b)));
    let previousKey: string | null = null;
    return sorted.map((point) => {
      const key = keyFor(point);
      const groupLabel = key === previousKey ? null : key;
      previousKey = key;
      return { point, groupLabel };
    });
  }, [collectionPoints, groupBy, supplierNameById, t]);

  if (!selectedOrganization) {
    return <Alert severity="info">{t('suppliers.collectionPoints.list.selectOrganization')}</Alert>;
  }

  const openCreate = () => {
    clearError();
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (point: SupplierCollectionPoint) => {
    clearError();
    setEditing(point);
    setFormOpen(true);
  };

  const saveCollectionPoint = async (input: SupplierCollectionPointInput) => {
    if (editing) {
      const { supplier_id, ...updateInput } = input;
      return Boolean(await updateCollectionPoint(selectedOrganization.id, editing.id, updateInput));
    }
    return Boolean(await createCollectionPoint(selectedOrganization.id, input));
  };

  const archive = async (point: SupplierCollectionPoint) => {
    const confirmed = await confirm({
      title: t('common.archive'),
      description: t('suppliers.collectionPoints.archiveConfirm', { name: point.name }),
      tone: 'danger',
    });
    if (confirmed) await archiveCollectionPoint(selectedOrganization.id, point.id);
  };

  return (
    <Box>
      <PageHeader
        icon={<PageIcon />}
        title={t('suppliers.collectionPoints.list.title')}
        subtitle={t('suppliers.collectionPoints.list.subtitle')}
        sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }}
        actions={canCreate ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <BulkImportControls
              onDownloadTemplate={() => downloadCollectionPointImportTemplate(orgId ?? '')}
              templateFilename="collection_points_import_template.xlsx"
              onImport={(file) => importCollectionPoints(orgId!, file)}
              onImported={() => orgId && void fetchCollectionPoints({ organization_id: orgId, page: view === 'map' ? 1 : page, limit: view === 'map' ? 500 : PAGE_SIZE, search: search.trim() || undefined, supplier_id: supplierId || undefined, material_id: materialId || undefined, city: city || undefined })}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              {t('suppliers.collectionPoints.list.addPoint')}
            </Button>
          </Stack>
        ) : undefined}
      />

      {error && !formOpen && <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs
        value={view}
        onChange={(_, next: CollectionPointsView) => setSearchParams({ view: next }, { replace: true })}
        sx={{ mb: 2 }}
      >
        <Tab value="table" icon={<TableIcon fontSize="small" />} iconPosition="start" label={t('suppliers.view.table')} />
        <Tab value="map" icon={<MapIcon fontSize="small" />} iconPosition="start" label={t('suppliers.view.map')} />
      </Tabs>

      {view === 'map' ? (
        <CollectionPointsMap
          points={collectionPoints}
          loading={isLoading}
          materialLabels={materialLabels}
          supplierOptions={supplierOptions.map((supplier) => ({ id: supplier.id, name: supplier.name }))}
          supplierNameById={supplierNameById}
          filters={{ search, supplierId, materialId }}
          onFiltersChange={(next) => {
            if (next.search !== undefined) setSearch(next.search);
            if (next.supplierId !== undefined) setSupplierId(next.supplierId);
            if (next.materialId !== undefined) setMaterialId(next.materialId);
          }}
          onOpenSupplier={(id) => navigate(`/suppliers/${id}`)}
        />
      ) : (
        <>
          <Card elevation={0} variant="outlined" sx={{ mb: 3 }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: hasActiveFilters ? '2fr 1fr 1fr 1fr auto' : '2fr 1fr 1fr 1fr' },
                  gap: 2,
                  alignItems: 'center',
                }}
              >
                <TextField
                  size="small"
                  label={t('suppliers.collectionPoints.list.searchPlaceholder')}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: `${APP_SHAPE.radiusSm}px` } }}
                />
                <FormControl size="small">
                  <InputLabel>{t('suppliers.collectionPoints.list.supplier')}</InputLabel>
                  <Select label={t('suppliers.collectionPoints.list.supplier')} value={supplierId} onChange={(event) => setSupplierId(event.target.value)}>
                    <MenuItem value="">{t('suppliers.collectionPoints.list.allSuppliers')}</MenuItem>
                    {supplierOptions.map((supplier) => <MenuItem key={supplier.id} value={supplier.id}>{supplier.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small">
                  <InputLabel>{t('suppliers.collectionPoints.list.wasteType')}</InputLabel>
                  <Select label={t('suppliers.collectionPoints.list.wasteType')} value={materialId} onChange={(event) => setMaterialId(event.target.value)}>
                    <MenuItem value="">{t('suppliers.collectionPoints.list.allWasteTypes')}</MenuItem>
                    {materialOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small">
                  <InputLabel>{t('suppliers.collectionPoints.list.city')}</InputLabel>
                  <Select label={t('suppliers.collectionPoints.list.city')} value={city} onChange={(event) => setCity(event.target.value)}>
                    <MenuItem value="">{t('suppliers.collectionPoints.list.allCities')}</MenuItem>
                    {cityOptions.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                  </Select>
                </FormControl>
                {hasActiveFilters && (
                  <Button size="small" onClick={() => { setSearch(''); setSupplierId(''); setMaterialId(''); setCity(''); }} sx={{ whiteSpace: 'nowrap' }}>
                    {t('suppliers.clearFilters')}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>

          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">{t('suppliers.collectionPoints.list.groupBy')}</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={groupBy}
              onChange={(_, next) => next && setGroupBy(next)}
            >
              <ToggleButton value="none">{t('suppliers.collectionPoints.list.groupByNone')}</ToggleButton>
              <ToggleButton value="city">{t('suppliers.collectionPoints.list.groupByCity')}</ToggleButton>
              <ToggleButton value="supplier">{t('suppliers.collectionPoints.list.groupBySupplier')}</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {isLoading ? <LoadingSkeleton variant="table" filters={false} columns={8} rows={6} /> : (
            <Paper elevation={2} sx={elevatedPanelSx}>
              <TableContainer sx={{ maxHeight: 'calc(100vh - 340px)' }}>
                <Table stickyHeader sx={{ '& .MuiTableCell-head': { py: 1.5, px: 2 }, '& .MuiTableCell-body': { py: 1.75, px: 2 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('suppliers.collectionPoints.table.name')}</TableCell>
                      <TableCell>{t('suppliers.collectionPoints.table.supplier')}</TableCell>
                      <TableCell>{t('suppliers.collectionPoints.table.city')}</TableCell>
                      <TableCell>{t('suppliers.collectionPoints.table.address')}</TableCell>
                      <TableCell>{t('suppliers.collectionPoints.table.contact')}</TableCell>
                      <TableCell>{t('suppliers.collectionPoints.table.wasteTypes')}</TableCell>
                      <TableCell>{t('suppliers.collectionPoints.table.labels')}</TableCell>
                      <TableCell align="right">{t('suppliers.collectionPoints.table.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupedRows.map(({ point, groupLabel }) => (
                      <Fragment key={point.id}>
                        {groupLabel !== null && (
                          <TableRow key={`group-${point.id}`}>
                            <TableCell colSpan={8} sx={{ bgcolor: 'action.hover', py: 1, border: 0 }}>
                              <Typography variant="subtitle2" fontWeight={700}>{groupLabel}</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow key={point.id} hover>
                        <TableCell><Typography fontWeight={700}>{point.name}</Typography></TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography variant="body2">{supplierNameById.get(point.supplier_id) ?? '—'}</Typography>
                            <Tooltip title={t('suppliers.collectionPoints.detail.openSupplier')}>
                              <IconButton size="small" onClick={() => navigate(`/suppliers/${point.supplier_id}`)}>
                                <OpenIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                        <TableCell>{point.city || '—'}</TableCell>
                        <TableCell>{point.address || '—'}</TableCell>
                        <TableCell>{point.contact_name || point.contact_phone || '—'}</TableCell>
                        <TableCell>{point.material_ids.length}</TableCell>
                        <TableCell><LabelChips labels={point.labels} /></TableCell>
                        <TableCell align="right">
                          {canEdit && (
                            <Tooltip title={t('suppliers.collectionPoints.table.edit')}>
                              <IconButton aria-label={t('suppliers.collectionPoints.table.edit')} size="small" onClick={() => openEdit(point)} sx={{ color: 'primary.main', width: 40, height: 40 }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canArchive && (
                            <Tooltip title={t('suppliers.collectionPoints.table.archive')}>
                              <IconButton aria-label={t('suppliers.collectionPoints.table.archive')} size="small" color="error" onClick={() => void archive(point)} sx={{ width: 40, height: 40 }}>
                                <ArchiveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                        </TableRow>
                      </Fragment>
                    ))}
                    {collectionPoints.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 8, border: 0 }}>
                          <Stack alignItems="center" spacing={1.5}>
                            <Box sx={{ width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(theme.palette.text.secondary, 0.08), color: 'text.secondary' }}>
                              <InboxIcon fontSize="large" />
                            </Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {hasActiveFilters ? t('suppliers.collectionPoints.list.filteredTitle') : t('suppliers.collectionPoints.list.emptyTitle')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                              {hasActiveFilters ? t('suppliers.collectionPoints.list.filteredHint') : t('suppliers.collectionPoints.list.emptyHint')}
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ px: 2.5, pb: 2.5 }}>
                <PaginationControl page={page} limit={PAGE_SIZE} total={total} onPageChange={setPage} />
              </Box>
            </Paper>
          )}
        </>
      )}

      <CollectionPointFormDialog
        open={formOpen}
        organizationId={selectedOrganization.id}
        point={editing}
        saving={isSaving}
        submitError={formOpen ? error : null}
        onClose={() => setFormOpen(false)}
        onSubmit={saveCollectionPoint}
      />
      {confirmDialog}
    </Box>
  );
}
